/**
 * @file 取り込んだ出題を、到達度モデルの単元と出典校から引けるようにする索引 (純関数)。
 *
 * @what: スキャン 1 問の索引 (`分野` / `単元` / `出典校` …) を、モデルの単元 (`taxonomy` に
 *   `分野/単元` を持つ) と突き合わせる。単元 → 出題、学校 → 出題の 2 方向を作る。
 * @why: 入試問題集は解く教材ではなく、いつまでに何がどの水準で問われるかを確かめるための
 *   資料である。計画の単元行からその場で実物を開けること、学校名からも引けることが要件
 *   (代表 2026-08-22)。突き合わせの鍵は語彙 (`分野/単元`) で、attainment_v1.yaml の
 *   `taxonomy` と ingest-materials の `types.yaml` が同じ語を使う前提に立つ。
 * @failure: 例外を投げない。`分野` か `単元` が欠けた行は索引に載らない (= 単元からは
 *   引けないが、リーダーでは読める)。
 */

import type { StudyScanItem } from "../api/scans";
import type { AttainmentModel, Unit } from "./types";

/** 出題 1 問と、それがどの資料の何問目かの出どころ。 */
export interface CorpusEntry {
  item: StudyScanItem;
  sourceId: string;
  sourceTitle: string;
}

/** 索引の `分野/単元` を語彙のキーにする。どちらか欠ければ null。 */
export function taxonomyKey(index: Record<string, string>): string | null {
  const field = index["分野"]?.trim();
  const unit = index["単元"]?.trim();
  if (!field || !unit) return null;
  return `${field}/${unit}`;
}

/** 語彙キー → 出題。単元行の展開はここから引く。 */
export function indexByTaxonomy(entries: readonly CorpusEntry[]): Map<string, CorpusEntry[]> {
  const map = new Map<string, CorpusEntry[]>();
  for (const e of entries) {
    const key = taxonomyKey(e.item.index);
    if (!key) continue;
    const bucket = map.get(key);
    if (bucket) bucket.push(e);
    else map.set(key, [e]);
  }
  return map;
}

/** 出典校 → 出題。学校名は索引の `出典校` をそのまま使う。 */
export function indexBySchool(entries: readonly CorpusEntry[]): Map<string, CorpusEntry[]> {
  const map = new Map<string, CorpusEntry[]>();
  for (const e of entries) {
    const school = e.item.index["出典校"]?.trim();
    if (!school) continue;
    const bucket = map.get(school);
    if (bucket) bucket.push(e);
    else map.set(school, [e]);
  }
  return map;
}

/**
 * 単元 1 つに対応する出題。単元は複数の語彙を持つので、重複を除いてページ順に並べる。
 */
export function entriesForUnit(
  byTaxonomy: Map<string, CorpusEntry[]>,
  unit: Pick<Unit, "taxonomy">,
): CorpusEntry[] {
  const seen = new Set<string>();
  const out: CorpusEntry[] = [];
  for (const key of unit.taxonomy) {
    for (const e of byTaxonomy.get(key) ?? []) {
      const id = `${e.sourceId}/${e.item.id}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(e);
    }
  }
  out.sort((a, b) =>
    a.sourceId === b.sourceId
      ? a.item.id.localeCompare(b.item.id)
      : a.sourceId.localeCompare(b.sourceId),
  );
  return out;
}

/** unit.id → 出題。計画の面で単元行ごとの件数を出すために一度だけ作る。 */
export function entriesByUnitId(
  model: AttainmentModel,
  byTaxonomy: Map<string, CorpusEntry[]>,
): Record<string, CorpusEntry[]> {
  const out: Record<string, CorpusEntry[]> = {};
  for (const u of model.units) {
    if (u.taxonomy.length === 0) continue;
    const entries = entriesForUnit(byTaxonomy, u);
    if (entries.length > 0) out[u.id] = entries;
  }
  return out;
}

/** 出題の一覧に出す 1 行分の要約 (図の説明が無ければ指示、次に設問 (過去問)、それも無ければ単元名)。 */
export function entrySummary(item: StudyScanItem): string {
  return (
    item.index["図の説明"]?.trim() ||
    item.index["指示"]?.trim() ||
    item.index["設問"]?.trim() ||
    item.index["単元"]?.trim() ||
    item.id
  );
}
